/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetMcpServerActivityRequest,
  GetMcpServerActivitySecurity,
} from "../models/operations/getmcpserveractivity.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetMcpServerActivityQuery,
  GetMcpServerActivityQueryData,
  prefetchGetMcpServerActivity,
  queryKeyGetMcpServerActivity,
} from "./getMcpServerActivity.core.js";
export {
  buildGetMcpServerActivityQuery,
  type GetMcpServerActivityQueryData,
  prefetchGetMcpServerActivity,
  queryKeyGetMcpServerActivity,
};

export type GetMcpServerActivityQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getMcpServerActivity telemetry
 *
 * @remarks
 * Get per-MCP-server tool-call activity for the Distribute MCP listing. Returns, for every MCP server with usage in the lookback window, its total and recent tool-call counts plus the last tool-call time, so the listing can flag servers that have never received a tool call or that have gone quiet.
 */
export function useGetMcpServerActivity(
  request: GetMcpServerActivityRequest,
  security?: GetMcpServerActivitySecurity | undefined,
  options?: QueryHookOptions<
    GetMcpServerActivityQueryData,
    GetMcpServerActivityQueryError
  >,
): UseQueryResult<
  GetMcpServerActivityQueryData,
  GetMcpServerActivityQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildGetMcpServerActivityQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getMcpServerActivity telemetry
 *
 * @remarks
 * Get per-MCP-server tool-call activity for the Distribute MCP listing. Returns, for every MCP server with usage in the lookback window, its total and recent tool-call counts plus the last tool-call time, so the listing can flag servers that have never received a tool call or that have gone quiet.
 */
export function useGetMcpServerActivitySuspense(
  request: GetMcpServerActivityRequest,
  security?: GetMcpServerActivitySecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetMcpServerActivityQueryData,
    GetMcpServerActivityQueryError
  >,
): UseSuspenseQueryResult<
  GetMcpServerActivityQueryData,
  GetMcpServerActivityQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetMcpServerActivityQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetMcpServerActivityData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: GetMcpServerActivityQueryData,
): GetMcpServerActivityQueryData | undefined {
  const key = queryKeyGetMcpServerActivity(...queryKeyBase);

  return client.setQueryData<GetMcpServerActivityQueryData>(key, data);
}

export function invalidateGetMcpServerActivity(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "telemetry",
      "getMcpServerActivity",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllGetMcpServerActivity(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "telemetry", "getMcpServerActivity"],
  });
}
